import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useFavoriteArtists } from '../../../hooks/useFavoriteArtists';
import { useFavoriteShops } from '../../../hooks/useFavoriteShops';
import { useAuth } from '../../../hooks/useAuth';
import { useEventStore } from '../../../hooks/useEventStore';
import { useEventReactions } from '../../../hooks/useEventReactions';
import { useShopStore } from '../../../hooks/useShopStore';
import { useArtistStore } from '../../../hooks/useArtistStore';
import { supabase } from '../../../lib/supabase';
import { FavoriteButton } from '../../../components/feature/FavoriteButton';

/* ─── Types ─── */
interface DbArtistInfo {
  userId: string;
  name: string;
  genre: string;
  location: string;
  color: string;
  image: string;
  platforms: string[];
}

interface DbShopInfo {
  id: string;
  shop_name: string;
  description: string;
  thumbnail: string;
  address: string;
  color: string;
  attributeName?: string;
  mainGenreName?: string;
}

interface EnrichedEvent {
  id: number;
  title: string;
  date: string;
  time: string;
  venue: string;
  category: string;
  color: string;
  thumbnail: string;
  description?: string;
  price?: string;
  attributeName?: string;
  displayColor?: string;
}

interface FavoritesTabProps {
  activeCategory: 'artists' | 'events' | 'nodes' | 'market';
}

/* ─── Section Header ─── */
function SectionHeader({
  icon,
  label,
  count,
  color,
}: {
  icon: string;
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: color, boxShadow: `0 0 8px ${color}` }}
        />
        <span className="text-white font-bold tracking-widest text-sm">{label}</span>
      </div>
      <div
        className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider"
        style={{
          background: `${color}15`,
          border: `1px solid ${color}30`,
          color,
        }}
      >
        <i className={`${icon} text-xs`} />
        {count}
      </div>
    </div>
  );
}

/* ─── Empty State ─── */
function EmptyState({
  icon,
  message,
  linkTo,
  linkLabel,
  color,
}: {
  icon: string;
  message: string;
  linkTo: string;
  linkLabel: string;
  color: string;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{ background: `${color}15`, border: `1px solid ${color}30` }}
      >
        <i className={`${icon} text-3xl`} style={{ color }} />
      </div>
      <p className="text-gray-500 text-sm tracking-wider">{message}</p>
      <button
        onClick={() => navigate(linkTo)}
        className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-xs font-bold tracking-wider transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
        style={{
          background: `${color}15`,
          border: `1px solid ${color}40`,
          color,
        }}
      >
        <i className="ri-search-line text-xs" />
        {linkLabel}
      </button>
    </div>
  );
}

/* ─── Artist Card ─── */
function ArtistCard({ artist, isDb }: { artist: any; isDb: boolean }) {
  const navigate = useNavigate();
  const color = artist.color || '#ff006e';

  return (
    <div
      className="flex-shrink-0 w-52 group cursor-pointer"
      onClick={() =>
        navigate(isDb ? `/artist/u/${artist.userId}` : `/artist/${artist.id}`)
      }
    >
      <div
        className="relative bg-[#111] rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-2"
        style={{ border: `1px solid ${color}33` }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${color}40`;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.boxShadow = 'none';
        }}
      >
        <div className="relative w-full h-52 overflow-hidden">
          {artist.image ? (
            <img
              src={artist.image}
              alt={artist.name}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${color}20, ${color}05)` }}
            >
              <i className="ri-user-line text-4xl" style={{ color: `${color}60` }} />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />
          {isDb && (
            <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full bg-teal-500/20 border border-teal-500/40">
              <span className="text-teal-400 text-[8px] font-bold tracking-wider">REGISTERED</span>
            </div>
          )}
        </div>
        <div className="px-3 pt-2 pb-3">
          <span className="text-[9px] tracking-widest block mb-0.5" style={{ color }}>
            {artist.location || 'JAPAN'}
          </span>
          <h4 className="text-white text-sm font-bold tracking-wide truncate mb-1">
            {artist.name}
          </h4>
          <div
            className="w-6 h-0.5"
            style={{ background: color, boxShadow: `0 0 6px ${color}` }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Shop Card ─── */
function ShopCard({ shop, isDb }: { shop: any; isDb: boolean }) {
  const navigate = useNavigate();
  const color = shop.color || '#00ff41';

  return (
    <div
      onClick={() => navigate(isDb ? `/shop/u/${shop.id}` : `/shop/${shop.id}`)}
      className="flex-shrink-0 w-64 rounded-xl border border-white/5 bg-black/40 overflow-hidden transition-all duration-300 cursor-pointer group hover:bg-white/[0.04] hover:border-white/15 hover:-translate-y-1"
    >
      <div className="relative w-full h-36 overflow-hidden">
        {shop.thumbnail ? (
          <img
            src={shop.thumbnail}
            alt={shop.shop_name || shop.name}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
            <i className="ri-store-2-line text-4xl text-gray-700" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {(shop.attributeName || shop.category) && (
          <span
            className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider backdrop-blur-sm"
            style={{
              background: `${color}30`,
              color,
              border: `1px solid ${color}40`,
            }}
          >
            {shop.attributeName || shop.category}
          </span>
        )}
        <div className="absolute top-2 left-2 z-20">
          <FavoriteButton
            artistId={isDb ? shop.id : String(shop.id)}
            artistType="shop"
            size="sm"
          />
        </div>
      </div>
      <div className="p-3">
        <h4 className="text-white text-sm font-bold tracking-wide truncate mb-1">
          {shop.shop_name || shop.name}
        </h4>
        <p className="text-gray-500 text-[10px] leading-relaxed line-clamp-2 mb-2">
          {shop.description}
        </p>
        <div className="flex items-center gap-1 pt-2 border-t border-white/5">
          <i className="ri-map-pin-line text-gray-600 text-[10px]" />
          <span className="text-gray-600 text-[10px] truncate">{shop.address}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Event Card ─── */
function EventCard({ event }: { event: EnrichedEvent }) {
  const navigate = useNavigate();
  const color = event.displayColor || event.color;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const weekdays = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
    return { month: months[d.getMonth()], day: String(d.getDate()), weekday: weekdays[d.getDay()] };
  };

  const { month, day, weekday } = formatDate(event.date);

  return (
    <div
      className="flex-shrink-0 w-72 rounded-xl border overflow-hidden transition-all duration-300 cursor-pointer group hover:-translate-y-1"
      style={{ borderColor: `${color}20`, background: '#0d0d0d' }}
      onClick={() => navigate(`/event/${event.id}`)}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${color}20`;
        (e.currentTarget as HTMLElement).style.borderColor = `${color}50`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
        (e.currentTarget as HTMLElement).style.borderColor = `${color}20`;
      }}
    >
      <div className="relative w-full h-40 overflow-hidden">
        <img
          src={event.thumbnail}
          alt={event.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-black/30 to-transparent" />
        {event.attributeName && (
          <span
            className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider backdrop-blur-sm"
            style={{ background: `${color}30`, color, border: `1px solid ${color}40` }}
          >
            {event.attributeName}
          </span>
        )}
        <div className="absolute bottom-2 left-2">
          <div
            className="w-10 h-12 rounded-lg flex flex-col items-center justify-center backdrop-blur-md"
            style={{ background: `${color}15`, border: `1px solid ${color}30` }}
          >
            <span className="text-[8px] tracking-wider" style={{ color }}>{month}</span>
            <span className="text-white text-base font-bold leading-none">{day}</span>
            <span className="text-gray-400 text-[7px] tracking-wider">{weekday}</span>
          </div>
        </div>
      </div>
      <div className="p-3">
        <h4 className="text-white text-sm font-bold tracking-wide truncate mb-1">{event.title}</h4>
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <i className="ri-map-pin-line text-gray-600 text-[10px]" />
            <span className="text-gray-500 text-[10px] truncate">{event.venue}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-time-line text-gray-600 text-[10px]" />
            <span className="text-gray-500 text-[10px]">{event.time}</span>
          </div>
          {event.price && (
            <div className="flex items-center gap-1">
              <i className="ri-ticket-line text-[10px]" style={{ color }} />
              <span className="text-[10px] font-bold" style={{ color }}>{event.price}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Grid Layout ─── */
function GridLayout({ children, count }: { children: React.ReactNode; count: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {children}
    </div>
  );
}

/* ─── Main Component ─── */
export default function FavoritesTab({ activeCategory }: FavoritesTabProps) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [registeredArtists, setRegisteredArtists] = useState<any[]>([]);
  const [dbShops, setDbShops] = useState<DbShopInfo[]>([]);
  const [enrichedFavoriteEvents, setEnrichedFavoriteEvents] = useState<EnrichedEvent[]>([]);
  const [enrichedRsvpEvents, setEnrichedRsvpEvents] = useState<EnrichedEvent[]>([]);
  const [isLoadingRegistered, setIsLoadingRegistered] = useState(false);
  const [isLoadingDbShops, setIsLoadingDbShops] = useState(false);

  const { favorites: artistFavorites, loading: isLoadingArtists } = useFavoriteArtists();
  const { favorites: shopFavorites, isLoading: isLoadingShops } = useFavoriteShops();
  const { favoriteEventIds, rsvpEventIds, loading: isLoadingEvents } = useEventReactions();
  const { events } = useEventStore();
  const { shops: mockShops } = useShopStore();
  const { artists } = useArtistStore();

  /* Fetch registered artists */
  useEffect(() => {
    if (!user) { setRegisteredArtists([]); return; }
    const ids = artistFavorites.filter(f => f.artist_type === 'user').map(f => f.artist_id);
    if (ids.length === 0) { setRegisteredArtists([]); return; }

    setIsLoadingRegistered(true);
    supabase.from('artist_layers').select('*').in('id', ids)
      .then(({ data }) => setRegisteredArtists(data || []))
      .catch(console.error)
      .finally(() => setIsLoadingRegistered(false));
  }, [user, artistFavorites]);

  /* Fetch DB shops */
  useEffect(() => {
    if (!user) { setDbShops([]); return; }
    const ids = shopFavorites.filter(f => f.shop_type === 'user').map(f => f.shop_id);
    if (ids.length === 0) { setDbShops([]); return; }

    setIsLoadingDbShops(true);
    supabase
      .from('shop_layers')
      .select('id, shop_name, description, thumbnail, address, color, attribute_id, main_genre_id')
      .in('id', ids)
      .then(async ({ data: shopsData }) => {
        if (!shopsData || shopsData.length === 0) { setDbShops([]); return; }
        const attrIds = [...new Set(shopsData.map(s => s.attribute_id).filter(Boolean))];
        const genreIds = [...new Set(shopsData.map(s => s.main_genre_id).filter(Boolean))];
        const [attrRes, genreRes] = await Promise.all([
          attrIds.length > 0 ? supabase.from('shop_attributes').select('id, name, color').in('id', attrIds) : { data: [] },
          genreIds.length > 0 ? supabase.from('shop_main_genres').select('id, name').in('id', genreIds) : { data: [] },
        ]);
        const attrMap = new Map((attrRes.data || []).map(a => [a.id, a]));
        const genreMap = new Map((genreRes.data || []).map(g => [g.id, g]));
        setDbShops(shopsData.map(s => ({
          id: s.id,
          shop_name: s.shop_name,
          description: s.description || '',
          thumbnail: s.thumbnail || '',
          address: s.address || '',
          color: attrMap.get(s.attribute_id)?.color || s.color || '#00ff41',
          attributeName: attrMap.get(s.attribute_id)?.name,
          mainGenreName: genreMap.get(s.main_genre_id)?.name,
        })));
      })
      .catch(console.error)
      .finally(() => setIsLoadingDbShops(false));
  }, [user, shopFavorites]);

  /* Enrich events */
  useEffect(() => {
    if (!user) { setEnrichedFavoriteEvents([]); setEnrichedRsvpEvents([]); return; }
    const favEvts = events.filter(e => favoriteEventIds.includes(String(e.id)));
    const rsvpEvts = events.filter(e => rsvpEventIds.includes(String(e.id)));
    const allEvts = [...favEvts, ...rsvpEvts];
    const attrIds = [...new Set(allEvts.map(e => e.attributeId).filter(Boolean))];

    if (attrIds.length === 0) {
      setEnrichedFavoriteEvents(favEvts.map(e => ({ ...e, displayColor: e.color })));
      setEnrichedRsvpEvents(rsvpEvts.map(e => ({ ...e, displayColor: e.color })));
      return;
    }

    supabase.from('event_attributes').select('id, name, color').in('id', attrIds)
      .then(({ data }) => {
        const attrMap = new Map((data || []).map(a => [a.id, a]));
        const enrich = (e: typeof events[0]): EnrichedEvent => {
          const attr = e.attributeId ? attrMap.get(e.attributeId) : null;
          return { ...e, attributeName: attr?.name, displayColor: attr?.color || e.color };
        };
        setEnrichedFavoriteEvents(favEvts.map(enrich));
        setEnrichedRsvpEvents(rsvpEvts.map(enrich));
      })
      .catch(() => {
        setEnrichedFavoriteEvents(favEvts.map(e => ({ ...e, displayColor: e.color })));
        setEnrichedRsvpEvents(rsvpEvts.map(e => ({ ...e, displayColor: e.color })));
      });
  }, [user, events, favoriteEventIds, rsvpEventIds]);

  /* Combine artists */
  const allFavoriteArtists = useMemo(() => {
    if (!user) return [];
    const mockIds = artistFavorites.filter(f => f.artist_type === 'mock').map(f => f.artist_id);
    const mockList = artists.filter(a => mockIds.includes(String(a.id))).map(a => ({ isDb: false, data: a }));
    const dbList = registeredArtists.map(a => ({
      isDb: true,
      data: {
        userId: a.id,
        name: a.artist_name || 'Unknown',
        genre: a.genre || '',
        location: a.location || '',
        color: a.accent_color || '#ff006e',
        image: a.profile_image_url || '',
        platforms: a.social_links ? Object.keys(a.social_links) : [],
      },
    }));
    return [...mockList, ...dbList];
  }, [user, artistFavorites, artists, registeredArtists]);

  /* Combine shops */
  const allFavoriteShops = useMemo(() => {
    if (!user) return [];
    const mockIds = shopFavorites.filter(f => f.shop_type === 'mock').map(f => f.shop_id);
    const mockList = mockShops.filter(s => mockIds.includes(String(s.id))).map(s => ({ isDb: false, data: s }));
    const dbList = dbShops.map(s => ({ isDb: true, data: s }));
    return [...mockList, ...dbList];
  }, [user, shopFavorites, mockShops, dbShops]);

  const isLoading = isLoadingArtists || isLoadingShops || isLoadingEvents || isLoadingRegistered || isLoadingDbShops;

  /* Not logged in */
  if (!user) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-7xl mx-auto px-4 py-16 flex flex-col items-center gap-4"
      >
        <div className="w-16 h-16 rounded-full flex items-center justify-center bg-pink-500/10 border border-pink-500/30">
          <i className="ri-heart-line text-3xl text-pink-400" />
        </div>
        <p className="text-gray-400 text-sm tracking-wider">ログインしてお気に入りを確認</p>
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-bold tracking-wider cursor-pointer transition-all hover:-translate-y-0.5"
          style={{ background: 'rgba(255,0,110,0.15)', border: '1px solid rgba(255,0,110,0.4)', color: '#ff006e' }}
        >
          <i className="ri-login-box-line" />
          ログイン
        </button>
      </motion.div>
    );
  }

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full max-w-7xl mx-auto px-4 py-16 flex items-center justify-center"
      >
        <i className="ri-loader-4-line text-2xl text-teal-400 animate-spin" />
      </motion.div>
    );
  }

  // カテゴリー別にコンテンツをレンダリング
  const renderCategoryContent = () => {
    switch (activeCategory) {
      case 'artists':
        return (
          <section>
            <SectionHeader icon="ri-user-star-line" label="FAVORITE ARTISTS" count={allFavoriteArtists.length} color="#ff006e" />
            {allFavoriteArtists.length === 0 ? (
              <EmptyState
                icon="ri-user-star-line"
                message="お気に入りのアーティストがまだありません"
                linkTo="/directory"
                linkLabel="アーティストを探す"
                color="#ff006e"
              />
            ) : (
              <GridLayout count={allFavoriteArtists.length}>
                {allFavoriteArtists.map((item) => (
                  <ArtistCard
                    key={item.isDb ? `db-${(item.data as DbArtistInfo).userId}` : `mock-${(item.data as any).id}`}
                    artist={item.data}
                    isDb={item.isDb}
                  />
                ))}
              </GridLayout>
            )}
          </section>
        );

      case 'nodes':
        return (
          <section>
            <SectionHeader icon="ri-store-3-line" label="FAVORITE NODES" count={allFavoriteShops.length} color="#00ff41" />
            {allFavoriteShops.length === 0 ? (
              <EmptyState
                icon="ri-store-3-line"
                message="お気に入りの店舗がまだありません"
                linkTo="/explore"
                linkLabel="店舗を探す"
                color="#00ff41"
              />
            ) : (
              <GridLayout count={allFavoriteShops.length}>
                {allFavoriteShops.map((item) => (
                  <ShopCard
                    key={item.isDb ? `db-${(item.data as DbShopInfo).id}` : `mock-${(item.data as any).id}`}
                    shop={item.data}
                    isDb={item.isDb}
                  />
                ))}
              </GridLayout>
            )}
          </section>
        );

      case 'events':
        const allEvents = [...enrichedFavoriteEvents, ...enrichedRsvpEvents];
        return (
          <div className="space-y-8">
            <section>
              <SectionHeader 
                icon="ri-calendar-heart-line" 
                label="SAVED & RSVP EVENTS" 
                count={allEvents.length} 
                color="#00d9ff" 
              />
              {allEvents.length === 0 ? (
                <EmptyState
                  icon="ri-calendar-event-line"
                  message="保存・参加予定のイベントがまだありません"
                  linkTo="/explore"
                  linkLabel="イベントを探す"
                  color="#00d9ff"
                />
              ) : (
                <GridLayout count={allEvents.length}>
                  {allEvents.map((evt) => (
                    <EventCard key={evt.id} event={evt} />
                  ))}
                </GridLayout>
              )}
            </section>
          </div>
        );

      case 'market':
        return (
          <section>
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <div className="relative mb-8">
                <div className="w-24 h-24 flex items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                  <i className="ri-shopping-bag-3-line text-5xl text-purple-400" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 flex items-center justify-center rounded-full bg-purple-500/20 border border-purple-500/40 animate-pulse">
                  <i className="ri-heart-line text-purple-400 text-sm" />
                </div>
              </div>
              
              <h3 className="text-white font-display text-2xl tracking-wider mb-3">COMING SOON</h3>
              <p className="text-gray-500 text-sm tracking-wide max-w-md">
                お気に入りグッズ・購入履歴の管理機能は現在開発中です
              </p>
              
              <div className="mt-8 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" style={{ animationDelay: '200ms' }} />
                <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" style={{ animationDelay: '400ms' }} />
              </div>
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-7xl mx-auto px-4 py-8"
    >
      {renderCategoryContent()}
    </motion.div>
  );
}